import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

type Language = "he" | "en";

type TranslationKeys = keyof typeof translations.he;

interface LanguageContextType {
  language: Language;
  toggleLanguage: () => void;
  t: (key: string) => string;
  getLanguageUrl: (path: string, targetLang?: Language) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined,
);

export const translations = {
  he: {
    // Navbar
    gallery: "גלרייה",
    "our.projects": "הפרוייקטים שלנו",
    "about.us": "קצת עלינו",
    "your.donation": "התרומה שלכם",

    // Homepage
    "how.it.started": "איך הכל התחיל?",
    "our.work.preview": "הצצה קטנה לעשייה שלנו:",
    "intro.text":
      "בעמותת ארזי חסד, כל חבילה היא צעד משמעותי לשינוי גדול עבור אלו שזקוקים לכך",

    // Gallery Page
    "gallery.title": "גלריית תמונות",
    image: "תמונה",
    page: "עמוד",
    of: "מתוך",
    "view.project": "צפה בפרויקט",

    // Payment Page
    "donation.title": "תרומה לארזי חסד",
    "donation.subtitle": "בזכותכם נוכל להמשיך לעזור למשפחות ויחידים",
    "donation.amount": "תרומה כללית",
    "other.amount": "סכום אחר",
    "payment.details": "פרטי תשלום",
    "select.project": "בחר פרויקט לתרומה",
    "general.donation": "תרומה כללית",
    "donating.to.project": "תורם לפרויקט",
    "donation.for.project": "תרומה לפרויקט",
    exit: "יציאה",
    "return.to.donation.page": "חזרה לדף התרומות",
    "full.name": "שם מלא",
    "full.name.placeholder": "הכנס את שמך המלא",
    phone: "טלפון",
    "phone.placeholder": "הכנס את מספר הטלפון שלך",
    email: "אימייל",
    "email.placeholder": "הכנס את כתובת האימייל שלך",
    notes: "הערות",
    "notes.placeholder": "הוסף הערה לתרומה (לא חובה)",
    "proceed.to.payment": "המשך לתשלום מאובטח",
    "proceed.to.payment.amount": "המשך לתשלום",

    // Footer
    "why.donate": "למה לתרום?",
    "donation.help.text":
      "אתם יכולים להיות אלה שמביאים שמחה, חיוך ותחושת הקלה למשפחות ולאנשים שמקבלים אחת מהחבילות שלנו.",
    "donation.impact":
      "כל תרומה, גדולה או קטנה, עוזרת לנו להגיע לעוד ועוד אנשים שיכולים להיעזר בהן!",
    "additional.info": "מידע נוסף",
    "tax.deductible":
      "לעמותה יש אישור מס הכנסה לעניין תרומות לפי סעיף 46 לפקודת מס הכנסה",
    "receipt.email": "קבלה תישלח למייל לאחר התרומה",
    "secure.payment": "התשלום מאובטח ומוצפן",
    organizationName: "ארזי חסד",
    ourProjects: "הפרויקטים שלנו",
    openingHours: "שעות פעילות",
    collaborationWithAssociations: "שיתופי פעולה עם עמותות",
    staffMembers: "חברי צוות",
    donations: "תרומות",

    // Language Toggle
    "switch.to.english": "EN",
    "switch.to.hebrew": "עב",

    // Projects
    "project.cleaning": "חומרי ניקוי",
    "project.tubshvat": "טו בשבט",
    "project.batmitzvah": "בת מצווה",
    "project.baby": "מוצרי תינוקות",
    "project.school": "ציוד בית ספר",
    "project.chanukah": "דמי חנוכה",
    "project.hospital": "ערכות טיפוח לבית החולים",
    "project.soldiers": "ערכות למגוייסים חדשים",

    // Email Contact Card
    "contact.us": "צור קשר",
    "your.name": "שם מלא",
    "name.placeholder": "הכנס את שמך המלא",
    "your.email": "כתובת מייל",
    "email.subject": "נושא",
    "email.subject.placeholder": "הכנס נושא להודעה",
    "subject.placeholder": "הכנס נושא להודעה",
    "email.message": "תוכן ההודעה",
    "email.message.placeholder": "הכנס את תוכן ההודעה שלך",
    "message.placeholder": "הכנס את תוכן ההודעה כאן",
    "email.send": "שלח",

    // Admin Login
    "admin.login": "כניסת מנהל",
    "admin.username": "שם משתמש",
    "admin.username.placeholder": "הכנס שם משתמש",
    "admin.password": "סיסמה",
    "admin.password.placeholder": "הכנס סיסמה",
    "admin.login.button": "התחבר",

    // Project Modal
    "project.description": "תיאור הפרוייקט",
    "project.gallery": "גלריית תמונות",
    "project.donate.button": "תרומה לפרוייקט זה",

    // About Page
    "about.mission.title": "השליחות שלנו",
    "about.mission.text":
      "ארזי חסד שואפת להביא שמחה, חיוך ותחושת הקלה למשפחות וליחידים, באמצעות אריזה וחלוקה של חבילות מותאמות לאירועים ולחגים.",
    "about.team.title": "הצוות שלנו",
    "about.team.text":
      "הצוות שלנו מורכב ממתנדבים מסורים הפועלים ללא לאות כדי להביא תקווה ועזרה למשפחות הזקוקות לכך.",
    "about.partners.title": "השותפים שלנו",
    "about.partners.text":
      "אנו גאים לעבוד בשיתוף פעולה עם ארגונים וחברות שמאמינים יחד איתנו בחשיבות הסיוע – בדיוק במקום ובזמן שבו הוא הכי נדרש.",

    // Contact Us
    "contact.us.button": "צור קשר",
    "contact.us.tooltip": "שלח לנו הודעה",

    // Project donation page
    "return.to.project": "חזרה לדף הפרויקט",

    // Custom Project Modal
    "your.project": "הפרויקט שלכם",
    "custom.project.description":
      "רוצים ליצור פרויקט משלכם?\nמי שמחפש דרך מיוחדת ומשמעותית לתרום לקהילה – אתם במקום הנכון!\nאנחנו מזמינים אתכם ליצור איתנו פרויקט מותאם אישית, שיכול להשתלב באירוע חברתי או משפחתי, או כחלק מפעילות צוות בעבודה או פרויקט בית ספר.\n\nביחד נבנה רעיון שמתאים ליכולות, לזמן וללב שלכם – ונעזור לכם להפוך אותו למציאות עם משמעות ושעושה טוב.\n\nצרו איתנו קשר ונשמח ללוות אתכם בכל שלב.",
    "create.your.project": "צור פרויקט משלך",
    "contact.for.project": "צור קשר ליצירת פרויקט",
    "close.modal": "סגור",
  },
  en: {
    // Navbar
    gallery: "Gallery",
    "our.projects": "Our Projects",
    "about.us": "About Us",
    "your.donation": "Donate",

    // Homepage
    "how.it.started": "How It All Started...",
    "our.work.preview": "A Preview of Our Work:",
    "intro.text":
      "At Arzei Chessed, every package is a significant step towards meaningful change for those in need",

    // Gallery Page
    "gallery.title": "Photo Gallery",
    image: "Image",
    page: "Page",
    of: "of",
    "view.project": "View Project",

    // Payment Page
    "donation.title": "Donate to Arzei Chessed",
    "donation.subtitle":
      "Your support helps us continue helping families and individuals",
    "donation.amount": "Donation Amount",
    "other.amount": "Other Amount",
    "payment.details": "Payment Details",
    "select.project": "Select Project to Support",
    "general.donation": "General Donation",
    "donating.to.project": "Donating to Project",
    "donation.for.project": "Donation for Project",
    exit: "Exit",
    "return.to.donation.page": "Back to Donations Page",
    "full.name": "Full Name",
    "full.name.placeholder": "Enter your full name",
    phone: "Phone",
    "phone.placeholder": "Enter your phone number",
    email: "Email",
    "email.placeholder": "Enter your email address",
    notes: "Notes",
    "notes.placeholder": "Add a note to your donation (optional)",
    "proceed.to.payment": "Proceed to Secure Payment",
    "proceed.to.payment.amount": "Proceed to Payment",

    // Footer
    "why.donate": "Why Donate?",
    "donation.help.text":
      "You can be the one who helps bring joy, smiles, and a sense of relief to families and individuals when they receive one of our packages.",
    "donation.impact":
      "Every donation, big or small, helps us reach that many more recipients!",
    "additional.info": "Additional Information",
    "tax.deductible":
      "Arzei Chessed is recognized as a public institution. Donations are tax-deductible in Israel under Section 46.",
    "receipt.email": "Your receipt will be sent to your email",
    "secure.payment": "The payment is secure and encrypted",
    organizationName: "Arzei Chessed",
    ourProjects: "Our Projects",
    openingHours: "Opening Hours",
    collaborationWithAssociations: "Partnership with Organizations",
    staffMembers: "Staff Members",
    donations: "Donations",

    // Language Toggle
    "switch.to.english": "EN",
    "switch.to.hebrew": "עב",

    // Projects
    "project.cleaning": "Cleaning Supplies",
    "project.tubshvat": "Tu BiShvat",
    "project.batmitzvah": "Bat Mitzvah",
    "project.baby": "Baby Essentials",
    "project.school": "School Supplies",
    "project.chanukah": "Hanukka Gelt",
    "project.hospital": "Hospital Care Packs",
    "project.soldiers": "New Recruits Kits",

    // Email Contact Card
    "contact.us": "Contact Us",
    "your.name": "Your Name",
    "name.placeholder": "Enter your full name",
    "your.email": "Your Email",
    "email.subject": "Subject",
    "email.subject.placeholder": "Enter message subject",
    "subject.placeholder": "Subject",
    "email.message": "Message",
    "email.message.placeholder": "Enter your message",
    "message.placeholder": "Message",
    "email.send": "Send",

    // Admin Login
    "admin.login": "Admin Login",
    "admin.username": "Username",
    "admin.username.placeholder": "Enter username",
    "admin.password": "Password",
    "admin.password.placeholder": "Enter password",
    "admin.login.button": "Login",

    // Project Modal
    "project.description": "Project Description",
    "project.gallery": "Project Gallery",
    "project.donate.button": "Donate to This Project",

    // About Page
    "about.mission.title": "Our mission",
    "about.mission.text":
      "Arzei Chessed strives to bring joy, smiles, and a sense of relief to families and individuals by packing and distributing thoughtfully tailored packages for holidays and special occasions.",
    "about.team.title": "Our team",
    "about.team.text":
      "Our team consists of dedicated volunteers who work tirelessly to bring hope and assistance to families who need it.",
    "about.partners.title": "Our partners",
    "about.partners.text":
      "We are proud to partner with organizations and companies that share our belief in the importance of providing assistance — exactly where and when it is most needed.",

    // Contact Us
    "contact.us.button": "Contact Us",
    "contact.us.tooltip": "Send us a message",

    // Project donation page
    "return.to.project": "Return to Project",

    // Custom Project Modal
    "your.project": "Your Project",
    "custom.project.description":
      "Want to create a project of your own?\nIf you're looking for a meaningful and unique way to give — you've come to the right place!\nWe invite you to work with us to design a personalized project that can be part of a social or family event, a team-building activity at work, or a school initiative.\n\nTogether, we'll develop an idea that fits your time, finances, and heart — and help you turn it into a meaningful reality that makes a true difference.\n\nGet in touch — we'd be happy to support you every step of the way.",
    "create.your.project": "Create Your Project",
    "contact.for.project": "Contact Us About Your Project",
    "close.modal": "Close",
  },
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [language, setLanguage] = useState<Language>("he");
  const location = useLocation();
  const navigate = useNavigate();

  // Set initial language based on URL path
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith("/en")) {
      setLanguage("en");
    } else {
      setLanguage("he");
    }
  }, [location.pathname]);

  // Toggle language and navigate to the corresponding URL
  const toggleLanguage = () => {
    const newLanguage = language === "he" ? "en" : "he";
    const newPath = getLanguageUrl(location.pathname, newLanguage);
    navigate(newPath);
    setLanguage(newLanguage);
  };

  // Helper function to generate language-specific URLs
  const getLanguageUrl = (
    path: string,
    targetLang: Language = language,
  ): string => {
    // Get the base path without language prefix
    let basePath = path;
    if (path.startsWith("/en/")) {
      basePath = path.substring(3); // Remove "/en/"
    } else if (path.startsWith("/en")) {
      basePath = path.substring(3) || "/"; // Handle "/en" case
    }

    // Make sure basePath starts with "/"
    if (!basePath.startsWith("/")) {
      basePath = "/" + basePath;
    }

    // Return the path with the appropriate language prefix
    return targetLang === "en" ? `/en${basePath}` : basePath;
  };

  const t = (key: string): string => {
    return (
      translations[language][key as TranslationKeys] ||
      translations[language]["email.subject.placeholder"] ||
      key
    );
  };

  return (
    <LanguageContext.Provider
      value={{ language, toggleLanguage, t, getLanguageUrl }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
